import React, { useState } from 'react';
import { useStatistics } from '../../../hooks/useStatistics';
import { mapApiDataToTableFormat, mapWeeklyDataToTableFormat, generateColumnHeaders } from '../../../utils/statisticsMapper';
import { generateWeeklyColumnHeaders } from '../../../utils/weeklyHelpers';
import LoadingSpinner from '../../controls/LoadingSpinner';
import ErrorMessage from '../../controls/ErrorMessage';
import './StatisticsPanel.css';

interface StatisticsRowData {
  label: string;
  mer: number;
  mar: number;
  lun: number;
  dom: number;
  sab: number;
  ven: number;
  gio: number;
}

interface StatisticsPanelProps {
  activeTab?: 'giornaliere' | 'settimanali';
  onTabChange?: (tab: 'giornaliere' | 'settimanali') => void;
  onDataChange?: (data: {
    dailyData: any[];
    weeklyData: any[];
    dailyDataPrevYear: any[];
    weeklyDataPrevYear: any[];
    isLoading: boolean;
    error: string | null;
  }) => void;
  onComparisonChange?: (showComparison: boolean) => void;
}

const StatisticsPanel: React.FC<StatisticsPanelProps> = ({ 
  activeTab: externalActiveTab,
  onTabChange,
  onDataChange,
  onComparisonChange
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<'giornaliere' | 'settimanali'>('giornaliere');
  const [showComparison, setShowComparison] = useState(false);
  
  // Usa activeTab esterno se fornito, altrimenti usa quello interno
  const activeTab = externalActiveTab || internalActiveTab;

  // Hook per recuperare i dati reali
  const { 
    dailyData, 
    weeklyData, 
    dailyDataPrevYear, 
    weeklyDataPrevYear, 
    isLoading, 
    error 
  } = useStatistics(7);

  const mockDailyData: StatisticsRowData[] = [
    { label: 'Accessi', mer: 15, mar: 23, lun: 18, dom: 8, sab: 12, ven: 31, gio: 27 },
    { label: 'Registrazioni', mer: 3, mar: 5, lun: 2, dom: 1, sab: 0, ven: 4, gio: 6 },
    { label: 'Diete Nuove', mer: 8, mar: 12, lun: 6, dom: 3, sab: 5, ven: 15, gio: 11 },
    { label: 'Diete Sett', mer: 4, mar: 7, lun: 3, dom: 2, sab: 1, ven: 9, gio: 8 },
    { label: 'Diete Gior', mer: 12, mar: 18, lun: 9, dom: 5, sab: 7, ven: 22, gio: 19 },
    { label: 'Conversioni', mer: 2, mar: 4, lun: 1, dom: 0, sab: 1, ven: 6, gio: 3 },
    { label: 'Alimenti', mer: 25, mar: 34, lun: 19, dom: 12, sab: 16, ven: 41, gio: 38 }
  ];

  const mockWeeklyData: StatisticsRowData[] = [
    { label: 'Accessi', mer: 134, mar: 156, lun: 98, dom: 87, sab: 145, ven: 178, gio: 123 },
    { label: 'Registrazioni', mer: 21, mar: 18, lun: 15, dom: 9, sab: 12, ven: 25, gio: 19 },
    { label: 'Diete Nuove', mer: 65, mar: 78, lun: 54, dom: 41, sab: 58, ven: 89, gio: 72 },
    { label: 'Diete Sett', mer: 34, mar: 42, lun: 28, dom: 21, sab: 31, ven: 48, gio: 39 },
    { label: 'Diete Gior', mer: 98, mar: 112, lun: 76, dom: 65, sab: 84, ven: 134, gio: 105 },
    { label: 'Conversioni', mer: 17, mar: 23, lun: 14, dom: 8, sab: 19, ven: 28, gio: 21 },
    { label: 'Alimenti', mer: 189, mar: 234, lun: 167, dom: 145, sab: 201, ven: 278, gio: 223 }
  ];

  // Dati dell'anno precedente (mock)
  const mockDailyDataPrevYear: StatisticsRowData[] = [
    { label: 'Accessi', mer: 18, mar: 19, lun: 14, dom: 6, sab: 15, ven: 28, gio: 22 },
    { label: 'Registrazioni', mer: 2, mar: 8, lun: 1, dom: 1, sab: 0, ven: 3, gio: 4 },
    { label: 'Diete Nuove', mer: 6, mar: 9, lun: 4, dom: 2, sab: 3, ven: 12, gio: 8 },
    { label: 'Diete Sett', mer: 3, mar: 5, lun: 2, dom: 1, sab: 1, ven: 7, gio: 6 },
    { label: 'Diete Gior', mer: 15, mar: 14, lun: 12, dom: 4, sab: 5, ven: 25, gio: 15 },
    { label: 'Conversioni', mer: 3, mar: 3, lun: 1, dom: 0, sab: 2, ven: 4, gio: 2 },
    { label: 'Alimenti', mer: 20, mar: 28, lun: 15, dom: 9, sab: 12, ven: 35, gio: 31 }
  ];

  const mockWeeklyDataPrevYear: StatisticsRowData[] = [
    { label: 'Accessi', mer: 140, mar: 128, lun: 105, dom: 71, sab: 118, ven: 190, gio: 98 },
    { label: 'Registrazioni', mer: 16, mar: 25, lun: 11, dom: 7, sab: 9, ven: 19, gio: 15 },
    { label: 'Diete Nuove', mer: 52, mar: 63, lun: 43, dom: 33, sab: 46, ven: 71, gio: 58 },
    { label: 'Diete Sett', mer: 27, mar: 34, lun: 22, dom: 17, sab: 25, ven: 38, gio: 31 },
    { label: 'Diete Gior', mer: 105, mar: 89, lun: 85, dom: 52, sab: 67, ven: 140, gio: 84 },
    { label: 'Conversioni', mer: 20, mar: 18, lun: 11, dom: 6, sab: 15, ven: 35, gio: 17 },
    { label: 'Alimenti', mer: 151, mar: 187, lun: 134, dom: 116, sab: 161, ven: 222, gio: 178 }
  ];

  // Determina quale dataset usare
  const getCurrentData = () => {
    if (error) {
      return {
        daily: mockDailyData,
        weekly: mockWeeklyData,
        dailyPrevYear: mockDailyDataPrevYear,
        weeklyPrevYear: mockWeeklyDataPrevYear
      };
    }

    return {
      daily: mapApiDataToTableFormat(dailyData, 0),
      weekly: mapWeeklyDataToTableFormat(weeklyData),
      dailyPrevYear: mapApiDataToTableFormat(dailyDataPrevYear, -1),
      weeklyPrevYear: mapWeeklyDataToTableFormat(weeklyDataPrevYear)
    };
  };

  const data = React.useMemo(() => getCurrentData(), [dailyData, weeklyData, dailyDataPrevYear, weeklyDataPrevYear, error]);

  // Notifica i cambiamenti dei dati al componente padre
  React.useEffect(() => {
    if (onDataChange) {
      onDataChange({
        dailyData: data.daily,
        weeklyData: data.weekly,
        dailyDataPrevYear: data.dailyPrevYear,
        weeklyDataPrevYear: data.weeklyPrevYear,
        isLoading,
        error
      });
    }
  }, [data, isLoading, error]);

  // Notifica i cambiamenti del confronto
  React.useEffect(() => {
    if (onComparisonChange) {
      onComparisonChange(showComparison);
    }
  }, [showComparison]);

  const renderDailyTable = () => {
    const columnHeaders = generateColumnHeaders();
    
    return (
      <div className="statistics-table">
        <div className="table-header">
          <div className="table-cell header-cell label-header"></div>
          {columnHeaders.map((header, index) => (
            <div key={index} className="table-cell header-cell">{header}</div>
          ))}
        </div>
      
      {data.daily.map((row, index) => (
        <div key={index} className="table-row">
          <div className="table-cell row-label">{row.label}</div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.mer}</div>
            {showComparison && data.dailyPrevYear[index] && (
              <div className="prev-year-value">
                {data.dailyPrevYear[index].mer}
                <span className={`trend ${row.mer >= data.dailyPrevYear[index].mer ? 'positive' : 'negative'}`}>
                  {row.mer >= data.dailyPrevYear[index].mer ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.mar}</div>
            {showComparison && data.dailyPrevYear[index] && (
              <div className="prev-year-value">
                {data.dailyPrevYear[index].mar}
                <span className={`trend ${row.mar >= data.dailyPrevYear[index].mar ? 'positive' : 'negative'}`}>
                  {row.mar >= data.dailyPrevYear[index].mar ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.lun}</div>
            {showComparison && data.dailyPrevYear[index] && (
              <div className="prev-year-value">
                {data.dailyPrevYear[index].lun}
                <span className={`trend ${row.lun >= data.dailyPrevYear[index].lun ? 'positive' : 'negative'}`}>
                  {row.lun >= data.dailyPrevYear[index].lun ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.dom}</div>
            {showComparison && data.dailyPrevYear[index] && (
              <div className="prev-year-value">
                {data.dailyPrevYear[index].dom}
                <span className={`trend ${row.dom >= data.dailyPrevYear[index].dom ? 'positive' : 'negative'}`}>
                  {row.dom >= data.dailyPrevYear[index].dom ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.sab}</div>
            {showComparison && data.dailyPrevYear[index] && (
              <div className="prev-year-value">
                {data.dailyPrevYear[index].sab}
                <span className={`trend ${row.sab >= data.dailyPrevYear[index].sab ? 'positive' : 'negative'}`}>
                  {row.sab >= data.dailyPrevYear[index].sab ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.ven}</div>
            {showComparison && data.dailyPrevYear[index] && (
              <div className="prev-year-value">
                {data.dailyPrevYear[index].ven}
                <span className={`trend ${row.ven >= data.dailyPrevYear[index].ven ? 'positive' : 'negative'}`}>
                  {row.ven >= data.dailyPrevYear[index].ven ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.gio}</div>
            {showComparison && data.dailyPrevYear[index] && (
              <div className="prev-year-value">
                {data.dailyPrevYear[index].gio}
                <span className={`trend ${row.gio >= data.dailyPrevYear[index].gio ? 'positive' : 'negative'}`}>
                  {row.gio >= data.dailyPrevYear[index].gio ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
      </div>
    );
  };

  const renderWeeklyTable = () => {
    const weeklyHeaders = generateWeeklyColumnHeaders();
    
    return (
      <div className="statistics-table">
        <div className="table-header">
          <div className="table-cell header-cell label-header"></div>
          {weeklyHeaders.map((header, index) => (
            <div key={index} className="table-cell header-cell">{header}</div>
          ))}
        </div>
      
      {data.weekly.map((row, index) => (
        <div key={index} className="table-row">
          <div className="table-cell row-label">{row.label}</div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.mer}</div>
            {showComparison && (
              <div className="prev-year-value">
                {data.weeklyPrevYear[index].mer}
                <span className={`trend ${row.mer >= data.weeklyPrevYear[index].mer ? 'positive' : 'negative'}`}>
                  {row.mer >= data.weeklyPrevYear[index].mer ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.mar}</div>
            {showComparison && (
              <div className="prev-year-value">
                {data.weeklyPrevYear[index].mar}
                <span className={`trend ${row.mar >= data.weeklyPrevYear[index].mar ? 'positive' : 'negative'}`}>
                  {row.mar >= data.weeklyPrevYear[index].mar ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.lun}</div>
            {showComparison && (
              <div className="prev-year-value">
                {data.weeklyPrevYear[index].lun}
                <span className={`trend ${row.lun >= data.weeklyPrevYear[index].lun ? 'positive' : 'negative'}`}>
                  {row.lun >= data.weeklyPrevYear[index].lun ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.dom}</div>
            {showComparison && (
              <div className="prev-year-value">
                {data.weeklyPrevYear[index].dom}
                <span className={`trend ${row.dom >= data.weeklyPrevYear[index].dom ? 'positive' : 'negative'}`}>
                  {row.dom >= data.weeklyPrevYear[index].dom ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.sab}</div>
            {showComparison && (
              <div className="prev-year-value">
                {data.weeklyPrevYear[index].sab}
                <span className={`trend ${row.sab >= data.weeklyPrevYear[index].sab ? 'positive' : 'negative'}`}>
                  {row.sab >= data.weeklyPrevYear[index].sab ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.ven}</div>
            {showComparison && (
              <div className="prev-year-value">
                {data.weeklyPrevYear[index].ven}
                <span className={`trend ${row.ven >= data.weeklyPrevYear[index].ven ? 'positive' : 'negative'}`}>
                  {row.ven >= data.weeklyPrevYear[index].ven ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
          <div className={`table-cell ${showComparison ? 'split-cell' : ''}`}>
            <div className="current-value">{row.gio}</div>
            {showComparison && (
              <div className="prev-year-value">
                {data.weeklyPrevYear[index].gio}
                <span className={`trend ${row.gio >= data.weeklyPrevYear[index].gio ? 'positive' : 'negative'}`}>
                  {row.gio >= data.weeklyPrevYear[index].gio ? '↗' : '↘'}
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
      </div>
    );
  };

  return (
    <div className="statistics-panel">
      <div className="panel-header">
        <div className="header-content">
          <h3>Statistiche</h3>
          <div className="panel-controls">
            <div className="comparison-toggle">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={showComparison}
                  onChange={(e) => setShowComparison(e.target.checked)}
                  className="toggle-checkbox"
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">Confronta con anno precedente</span>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      <div className="panel-tabs">
        <button 
          className={`tab-button ${activeTab === 'giornaliere' ? 'active' : ''}`}
          onClick={() => {
            const newTab = 'giornaliere';
            if (onTabChange) {
              onTabChange(newTab);
            } else {
              setInternalActiveTab(newTab);
            }
          }}
        >
          Giornaliere
        </button>
        <button 
          className={`tab-button ${activeTab === 'settimanali' ? 'active' : ''}`}
          onClick={() => {
            const newTab = 'settimanali';
            if (onTabChange) {
              onTabChange(newTab);
            } else {
              setInternalActiveTab(newTab);
            }
          }}
        >
          Settimanali
        </button>
      </div>

      <div className="panel-content">
        {isLoading ? (
          <div className="loading-container">
            <LoadingSpinner />
            <p>Caricamento statistiche...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <ErrorMessage message={error} />
            <p>Visualizzazione dati mock come fallback</p>
          </div>
        ) : (
          activeTab === 'giornaliere' ? renderDailyTable() : renderWeeklyTable()
        )}
      </div>
    </div>
  );
};

export default StatisticsPanel;